import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Box, Cable, Info, Loader2, RotateCw, X } from 'lucide-react';
import { FACADE_BOX_PORTS, summarizeFacadeCocon } from '@uniticket/shared';
import type { CapacityUnit, FacadeBoxAddress, FacadeBoxesResponse, MduApartmentDto } from '@uniticket/shared';
import { Button } from '@/components/ui/button';
import { addressLine } from '@/components/dmp/DmpConsolePanel';
import { fetchFacadeBoxes, type FacadeCoconState } from '@/hooks/useAddressValidation';
import { cn } from '@/lib/utils';

// N23 — l'« En savoir plus » de la capacité : le boîtier façade tel que COCON le dessine (demande du 15-09).
//
// COCON sait quelles fibres arrivent au boîtier, voisins compris : c'est ce que cette modale montre.
//
// ⛔ DEPUIS C0.1 (17-09), CE N'EST PLUS UNE INFORMATION À CÔTÉ : c'est LE calcul des adresses QD / QDX. La
// décision du 15-09 (« la pastille garde son calcul ») est renversée — sur le JVID 10328760 la pastille disait 4
// pour un boîtier plein. Le « Disponible » d'ici et le nombre de la pastille sortent tous deux de
// `summarizeFacadeCocon`, sur la même lecture quand la page l'a faite (`cocon`) : ils ne peuvent plus diverger.
//
// Les trois statuts, tels que le user les a définis :
//   Connecté   = une fibre relie ce JVID au boîtier dans COCON (≠ client actif : la légende le précise) ;
//   Prévu      = une unité de l'adresse EN SCOPE (V15 ou V2, non retirée) sans aucune fibre — elle prendra une
//                position. Resserré le 17-09 : une unité hors scope ne prendra jamais de place ;
//   Disponible = position sans JVID : 8 − connectés − prévus.
// Avec PLUSIEURS boîtiers, on ne sait pas lequel recevra un prévu : chaque boîtier montre ses connectés et ses
// positions libres, et les prévus sont listés une fois pour l'adresse, déduits du total (décision du 15-09).
//
// COCON ne donne NI numéro de port NI état de fibre : les positions 1 à 8 sont un ordre d'affichage, et la
// modale le dit aussi.

type SlotKind = 'connected' | 'planned' | 'free';
type RowKind = SlotKind | 'elsewhere';

const TONE: Record<RowKind, { dot: string; pill: string; line: string }> = {
  connected: { dot: 'bg-emerald-500', pill: 'border-emerald-200 bg-emerald-50 text-emerald-700', line: 'bg-emerald-300' },
  free: { dot: 'bg-blue-500', pill: 'border-blue-200 bg-blue-50 text-blue-700', line: 'bg-blue-300' },
  planned: { dot: 'bg-slate-400', pill: 'border-slate-200 bg-slate-100 text-slate-600', line: 'bg-slate-300' },
  elsewhere: { dot: 'bg-amber-400', pill: 'border-amber-200 bg-amber-50 text-amber-700', line: 'bg-amber-300' },
};

const collator = new Intl.Collator('fr', { numeric: true, sensitivity: 'base' });

const streetLine = (a: FacadeBoxAddress) => addressLine(a.streetName, a.houseNumber, a.houseNumberExtension);

/** « Rue Puissant 56 - 001 » : l'adresse et sa boîte, comme sur la maquette. */
const addressWithBox = (a: FacadeBoxAddress) => [streetLine(a), a.busNumber?.trim()].filter(Boolean).join(' - ');

/** Par adresse puis par boîte, numéros compris dans l'ordre naturel (2 avant 10). JVID sans adresse en dernier. */
function sortByAddress(jvids: number[], byJvid: Map<number, FacadeBoxAddress>): number[] {
  return [...jvids].sort((x, y) => {
    const ax = byJvid.get(x);
    const ay = byJvid.get(y);
    if (!ax || !ay) return ax ? -1 : ay ? 1 : x - y;
    return collator.compare(streetLine(ax), streetLine(ay))
      || collator.compare(ax.busNumber ?? '', ay.busNumber ?? '')
      || x - y;
  });
}

/** Les positions d'un boîtier : les connectés, puis les prévus qu'on peut y placer, puis les places libres.
 *  Un boîtier que COCON dit plus que plein garde TOUTES ses fibres — les masquer cacherait l'anomalie. */
function slotsFor(connected: number[], planned: number[]): { kind: SlotKind; jvid: number | null }[] {
  const slots: { kind: SlotKind; jvid: number | null }[] = connected.map((jvid) => ({ kind: 'connected', jvid }));
  for (const jvid of planned) if (slots.length < FACADE_BOX_PORTS) slots.push({ kind: 'planned', jvid });
  while (slots.length < FACADE_BOX_PORTS) slots.push({ kind: 'free', jvid: null });
  return slots;
}

function Dot({ kind }: { kind: RowKind }) {
  return <span className={cn('inline-block h-2 w-2 shrink-0 rounded-full', TONE[kind].dot)} />;
}

export function FacadeBoxModal({
  apartments, addressLabel, cocon, onClose,
}: {
  apartments: MduApartmentDto[];
  addressLabel: string;
  /** La lecture COCON de la page (adresses QD / QDX) : la modale n'en refait pas une. Absente (autres cadrans),
   *  la modale lit COCON elle-même, comme avant C0.1. */
  cocon?: FacadeCoconState;
  onClose: () => void;
}) {
  const { t } = useTranslation(['tickets']);
  const k = (key: string, o?: Record<string, unknown>) => t(`tickets:validation.capacity.facade.${key}`, o);

  const [ownData, setOwnData] = useState<FacadeBoxesResponse | null>(null);
  const [ownFailed, setOwnFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Les unités de l'adresse, dans la forme que `summarizeFacadeCocon` attend — la même projection que
  // `capacityUnitsFromApartments` (CapacityPanel), recopiée pour ne pas créer d'import circulaire. Les lignes
  // synthétiques (boîte déclarée par un opérateur, absente du DMP) n'ont pas de JVID à chercher dans COCON.
  const units = useMemo<CapacityUnit[]>(() => apartments.map((a) => ({
    jvid: a.added ? null : a.jvid,
    scopelistV2: a.attributes?.scopelistV2 ?? null,
    scopelistV15: a.attributes?.scopelistV15 ?? null,
    outOfScopeAt: a.attributes?.outOfScopeAt ?? null,
  })), [apartments]);
  const ownJvids = useMemo(
    () => [...new Set(units.map((u) => u.jvid).filter((j): j is number => j != null && Number.isInteger(j) && j > 0))],
    [units],
  );
  const own = useMemo(() => new Set(ownJvids), [ownJvids]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (cocon) return; // la page a déjà lu COCON pour cette adresse
    let cancelled = false;
    setOwnData(null);
    setOwnFailed(false);
    if (ownJvids.length === 0) {
      setOwnData({ boxes: [], linkedElsewhere: [], unlinked: [], addresses: [] });
      return;
    }
    fetchFacadeBoxes(ownJvids)
      .then((res) => { if (!cancelled) setOwnData(res.data); })
      .catch(() => { if (!cancelled) setOwnFailed(true); });
    return () => { cancelled = true; };
  }, [ownJvids, attempt, cocon]);

  const data = cocon ? cocon.data : ownData;
  const failed = cocon ? cocon.status === 'error' : ownFailed;
  const retry = () => (cocon ? cocon.retry() : setAttempt((n) => n + 1));

  const view = useMemo(() => {
    if (!data) return null;
    // Le MÊME résumé que la pastille : boîtiers de CETTE adresse (la lecture de la page couvre tout le ticket),
    // prévues en scope seulement.
    const s = summarizeFacadeCocon(data, units);
    const byJvid = new Map(data.addresses.map((a) => [a.jvid, a]));
    const boxes = s.boxes.map((b) => ({ name: b.name, jvids: sortByAddress(b.jvids, byJvid) }));
    const planned = sortByAddress(s.planned, byJvid);
    const ownElsewhere = data.linkedElsewhere.filter((l) => own.has(l.jvid));
    const elsewhere = sortByAddress(ownElsewhere.map((l) => l.jvid), byJvid)
      .map((jvid) => ownElsewhere.find((l) => l.jvid === jvid)!);
    return {
      byJvid, boxes, planned, elsewhere,
      connected: s.connected,
      capacity: s.ports,
      available: s.available,
      // Plus de prévus que de places : le seul cas où la modale a quelque chose d'alarmant à dire.
      missing: s.missing,
    };
  }, [data, units, own]);

  const hasBoxes = !!view && view.boxes.length > 0;

  const subtitle = failed
    ? k('error')
    : !view
      ? k('loading')
      : !hasBoxes
        ? k('none')
        : view.boxes.length === 1
          ? k('subtitleOne', {
            name: view.boxes[0].name, available: view.available, capacity: view.capacity,
            connected: view.connected, planned: view.planned.length,
          })
          : k('subtitleMany', {
            boxes: view.boxes.length, available: view.available, capacity: view.capacity,
            connected: view.connected, planned: view.planned.length,
          });

  const detailRows: { jvid: number; kind: RowKind; box: string | null; enclosure: string | null }[] = view
    ? [
      ...view.boxes.flatMap((b) => b.jvids.map((jvid) => ({ jvid, kind: 'connected' as const, box: b.name, enclosure: null }))),
      ...view.planned.map((jvid) => ({ jvid, kind: 'planned' as const, box: null, enclosure: null })),
      ...view.elsewhere.map((l) => ({ jvid: l.jvid, kind: 'elsewhere' as const, box: null, enclosure: l.enclosure })),
    ]
    : [];

  return createPortal(
    // z-[60] : cette modale s'ouvre PAR-DESSUS celle de la capacité (z-50), qui reste montée dessous.
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px] animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={k('title')}
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="rounded-xl bg-rose-50 p-2">
              <Cable className="h-5 w-5 text-brand-rose" />
            </span>
            <div className="min-w-0">
              <p className="text-base font-bold text-slate-800">{k('title')}</p>
              <p className={cn('text-xs', failed ? 'text-rose-700' : 'text-slate-500')}>{subtitle}</p>
              <p className="text-[11px] text-slate-400">{addressLabel}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={k('close')}
            className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {failed && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2" role="alert">
              <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
              <span className="text-xs text-rose-800">{k('error')}</span>
              <Button size="sm" variant="outline" className="ml-auto" onClick={retry}>
                <RotateCw className="mr-1.5 h-3.5 w-3.5" />
                {k('retry')}
              </Button>
            </div>
          )}

          {!failed && !view && (
            <p className="flex items-center justify-center gap-2 py-12 text-xs text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {k('loading')}
            </p>
          )}

          {view && !hasBoxes && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-700" role="note">
              <p>{k('none')}</p>
              {view.elsewhere.length > 0 && (
                <p className="mt-1 text-slate-500">
                  {k('noneElsewhere', {
                    count: view.elsewhere.length,
                    list: [...new Set(view.elsewhere.map((l) => l.enclosure))].join(', '),
                  })}
                </p>
              )}
            </div>
          )}

          {view && hasBoxes && (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
              {/* Le boîtier dessiné. Un seul boîtier porte aussi les prévus ; à plusieurs, ils vont en dessous. */}
              <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                {view.boxes.map((box) => {
                  const slots = slotsFor(box.jvids, view.boxes.length === 1 ? view.planned : []);
                  return (
                    <div key={box.name}>
                      <div className="flex items-center gap-2.5">
                        <span className="rounded-lg border border-slate-200 bg-white p-2">
                          <Box className="h-5 w-5 text-slate-500" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-800">{k('boxTitle')}</p>
                          <p className="truncate text-xs text-slate-500" title={box.name}>{box.name}</p>
                        </div>
                        <span className="ml-auto shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                          {k('boxLinked', { connected: box.jvids.length, ports: FACADE_BOX_PORTS })}
                        </span>
                      </div>
                      <ol className="mt-3 space-y-1.5">
                        {slots.map((slot, i) => {
                          const a = slot.jvid != null ? view.byJvid.get(slot.jvid) : undefined;
                          return (
                            <li key={`${slot.kind}-${slot.jvid ?? 'free'}-${i}`} className="flex items-center gap-2">
                              <span
                                className={cn(
                                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border bg-white text-[11px] font-bold',
                                  i >= FACADE_BOX_PORTS ? 'border-rose-300 text-rose-600' : 'border-slate-200 text-slate-600',
                                )}
                              >
                                {i + 1}
                              </span>
                              <span className={cn('h-0.5 w-4 shrink-0 rounded', TONE[slot.kind].line)} />
                              <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
                                <Dot kind={slot.kind} />
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-semibold text-slate-800">
                                    {slot.jvid != null ? `JVID ${slot.jvid}` : k('freeSlot')}
                                  </p>
                                  {slot.jvid != null && (
                                    <p className="truncate text-[11px] text-slate-500">
                                      {a ? addressWithBox(a) : '—'}
                                      {!own.has(slot.jvid) && <span className="text-slate-400"> · {k('neighbour')}</span>}
                                    </p>
                                  )}
                                </div>
                                <span className={cn('shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold', TONE[slot.kind].pill)}>
                                  {/* À plusieurs boîtiers, les prévus ne sont pas encore déduits d'une case précise :
                                      appeler ces cases « Disponible » contredirait le compteur, qui les déduit. */}
                                  {slot.kind === 'free' && view.boxes.length > 1 ? k('status.open') : k(`status.${slot.kind}`)}
                                </span>
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                      {box.jvids.length > FACADE_BOX_PORTS && (
                        <p className="mt-2 text-[11px] text-rose-700">{k('overfull')}</p>
                      )}
                    </div>
                  );
                })}

                {view.boxes.length > 1 && view.planned.length > 0 && (
                  <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                      <Dot kind="planned" />
                      {k('plannedTitle', { count: view.planned.length })}
                    </p>
                    <p className="mt-0.5 text-[11px] text-slate-500">{k('plannedHint')}</p>
                    <ul className="mt-1.5 space-y-0.5">
                      {view.planned.map((jvid) => {
                        const a = view.byJvid.get(jvid);
                        return (
                          <li key={jvid} className="truncate text-[11px] text-slate-600">
                            JVID {jvid} · {a ? addressWithBox(a) : '—'}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {view.missing > 0 && (
                  <p className="flex items-start gap-1.5 rounded-md border border-rose-300 bg-rose-50 px-2.5 py-2 text-[11px] text-rose-800" role="note">
                    <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-rose-600" />
                    {k('missing', { count: view.missing })}
                  </p>
                )}

                <p className="flex items-start gap-1.5 text-[11px] leading-snug text-slate-500">
                  <Info className="mt-px h-3 w-3 shrink-0" />
                  {k('portsNote', { ports: FACADE_BOX_PORTS })}
                </p>
              </div>

              {/* Le détail : chaque fibre du boîtier (voisins compris), puis les prévus, puis ce qui est relié ailleurs. */}
              <div className="min-w-0 rounded-xl border border-slate-200 p-3">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <p className="text-sm font-semibold text-slate-800">{k('detailTitle')}</p>
                  <div className="ml-auto flex flex-wrap items-center gap-3 text-[11px] text-slate-600">
                    <span className="inline-flex items-center gap-1.5"><Dot kind="connected" />{k('legendConnected', { count: view.connected })}</span>
                    <span className="inline-flex items-center gap-1.5"><Dot kind="free" />{k('legendFree', { count: view.available })}</span>
                    <span className="inline-flex items-center gap-1.5"><Dot kind="planned" />{k('legendPlanned', { count: view.planned.length })}</span>
                  </div>
                </div>
                <p className="mt-1 text-[10px] text-slate-400">{k('connectedMeaning')}</p>

                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-left text-slate-500">
                      <tr className="border-b border-slate-100">
                        <th className="py-2 pr-3 font-semibold">{k('colNo')}</th>
                        <th className="py-2 pr-3 font-semibold">JVID</th>
                        <th className="py-2 pr-3 font-semibold">{k('colAddress')}</th>
                        <th className="py-2 pr-3 font-semibold">{k('colBox')}</th>
                        <th className="py-2 pr-3 font-semibold">{k('colPostalCode')}</th>
                        <th className="py-2 pr-3 font-semibold">{k('colCity')}</th>
                        {view.boxes.length > 1 && <th className="py-2 pr-3 font-semibold">{k('colEnclosure')}</th>}
                        <th className="py-2 font-semibold">{k('colStatus')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailRows.map((row, i) => {
                        const a = view.byJvid.get(row.jvid);
                        return (
                          <tr key={`${row.kind}-${row.jvid}-${i}`} className="border-b border-slate-50 last:border-0">
                            <td className="py-2 pr-3">
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600">
                                {i + 1}
                              </span>
                            </td>
                            <td className="py-2 pr-3 font-semibold text-slate-800">{row.jvid}</td>
                            <td className="py-2 pr-3 text-slate-700">
                              {a ? streetLine(a) : '—'}
                              {!own.has(row.jvid) && <span className="text-slate-400"> · {k('neighbour')}</span>}
                            </td>
                            <td className="py-2 pr-3 font-mono text-slate-700">{a?.busNumber?.trim() || '—'}</td>
                            <td className="py-2 pr-3 text-slate-700">{a?.postalCode || '—'}</td>
                            <td className="py-2 pr-3 text-slate-700">{a?.city || '—'}</td>
                            {view.boxes.length > 1 && <td className="py-2 pr-3 text-slate-500">{row.box ?? '—'}</td>}
                            <td className="py-2">
                              <span
                                className={cn('rounded-full border px-2 py-0.5 text-[10px] font-semibold', TONE[row.kind].pill)}
                                title={row.enclosure ? k('elsewhereTitle', { enclosure: row.enclosure }) : undefined}
                              >
                                {k(`status.${row.kind}`)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-slate-100 px-5 py-3">
          {view && hasBoxes && (
            <>
              <div className="flex items-center gap-2.5">
                <Cable className="h-5 w-5 text-brand-rose" />
                <div>
                  <p className="text-xs font-semibold text-slate-700">{k('totalCapacity')}</p>
                  <p className="text-[11px] text-slate-500">
                    {k('totalFibers', { capacity: view.capacity, boxes: view.boxes.length })}
                  </p>
                </div>
              </div>
              {([
                ['connected', k('counterConnected'), view.connected, null],
                ['free', k('counterFree'), view.available, k('counterFreeHint')],
                ['planned', k('counterPlanned'), view.planned.length, k('counterPlannedHint')],
              ] as const).map(([kind, title, value, hint]) => (
                <div key={kind} className="flex items-start gap-2">
                  <span className="mt-1"><Dot kind={kind} /></span>
                  <div>
                    <p className="text-xs font-semibold text-slate-700">{title}</p>
                    <p className="text-sm text-slate-800">{value} / {view.capacity}</p>
                    {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
                  </div>
                </div>
              ))}
            </>
          )}
          <div className="ml-auto flex items-center gap-3">
            <p className="text-[10px] italic text-slate-400">{k('informative')}</p>
            <Button size="sm" variant="outline" onClick={onClose}>{k('close')}</Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
